<template>
  <div class="mx-auto my-4">
    <ValidationObserver v-slot="{ invalid, validate, errors }">
      <form name="RideForm" @submit.prevent="validate().then(() => onSubmit(invalid))">
        <div class="container">
          <div class="mb-4">
            <ValidationProvider rules="required" name="date of trip" v-slot="{ errors }">
            <CalendarField
              class="w-1/3 pe-5 pb-2"
              :min="today()"
              :max="maxDate()"
              v-model="formValues.tripDate">
              Date of Trip
            </CalendarField>
            <span
              class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
              name="error-text">
              {{ errors[0] }}
            </span>
            </ValidationProvider>
          </div>
          <div class="my-4">
            <label class="text-left block text-sm leading-6 text-gray-900">
              <input
                type="checkbox"
                v-model="isOtrA"
                class="mr-2"
              />
              Is this an OTR-A (One-time Ride - Alternative) with the SAME service provider?
            </label>
          </div>
        </div>
        <div class="container">
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-7 pb-4">
            <div class="flex flex-col">
              <label class="text-left block font-bold mb-2">Requesting District</label>

              <ValidationProvider rules="required" name="requesting district" v-slot="{ errors }">
                <v-select
                  class="border-gray-300 w-full"
                  v-model="selectedDistrict"
                  :options="districtList"
                  label="label"
                  :disabled="false"
                  @input="setDistrict()"
                  placeholder="Please select or type school or site destination name and then PRESS ENTER"
                >
                </v-select>
                <span
                  class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                  name="error-text">
                  {{ errors[0] }}
                </span>
              </ValidationProvider>
            </div>
          </div>
        </div>
        <div class="container" v-if="!isOtrA">
          <div class="w-1/3 pe-5 pb-2">
            <label class="text-left block font-bold mb-2">Designated Vendor</label>
            <ValidationProvider rules="required" name="designated vendor" v-slot="{ errors }">
              <v-select
                class="w-full pb-4"
                v-model="formValues.vendorDesignated"
                :options="vendorOptions"
                label="label"
                :key="vendor.id"
                placeholder="Select Vendor"
                track-by="value"
                :reduce="vendor => vendor.value"
              />
            <span
              class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
              name="error-text">
              {{ errors[0] }}
            </span>
          </ValidationProvider>
          </div>
        </div>
        <div class="container">
          <label class="text-left block font-bold mb-4">Student Info</label>
          <div class="grid grid-cols-3 gap-7"> 
            <TextField
              class="pb-2 w-full"
              :placeholder="'First Name'"
              v-model="formValues.firstName"
              :disabled="true">
            </TextField>
            <TextField
              class="pb-4 w-full"
              :placeholder="'Last Name'"
              v-model="formValues.lastName"
              :disabled="true">
            </TextField>
          </div>
        </div>
        <div class="container">
          <div class="grid grid-cols-3 gap-7"> 
            <SelectionField
              class="pb-2" 
              v-model="formValues.grade"
              :options="formattedGradeList"
              @input="onGradeChange"
              >
              Grade
            </SelectionField>
          </div>
        </div>
        <div class="container">
          <StudentAddress
            class="pb-4"
            id="Student"
            label="Student Address"
            :defaultValue="defaultValues.address"
            v-model="formValues.address">
          </StudentAddress>
        </div>
        <div class="container">
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-7 pb-4">
            <div class="flex flex-col">
              <label class="text-left block font-bold mb-2">School or Site Destination Name</label>

              <ValidationProvider rules="required" name="school or program name" v-slot="{ errors }">
                <v-select
                  class="border-gray-300 w-full"
                  v-model="selectedSchool"
                  :options="schoolList"
                  label="label"
                  :disabled="true"
                  @input="setSchool()"
                  placeholder="Please select or type school or site destination name and then PRESS ENTER"
                >
                </v-select>
                <span class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1" name="error-text">
                  {{ errors[0] }}
                </span>
              </ValidationProvider>
            </div>
          </div>
        </div>
        <div class="container">
          <p class="text-left block font-bold mb-2">
            Is this an inbound (to school) request or an outbound (from school) request?
          </p>
          <ValidationProvider rules="required" name="inbound/outbound selection" v-slot="{ errors }">
            <div class="flex items-center">
              <label class="mr-4">
                <input type="radio" v-model="formValues.isInbound" :value="true" />
                Inbound
              </label>
              <label>
                <input type="radio" v-model="formValues.isInbound" :value="false" />
                Outbound
              </label>
            </div>
            <span
              class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
              name="error-text"
            >
              {{ errors[0] }}
            </span>
          </ValidationProvider>
          <div class="mb-4"></div>
        </div>
        <div class="container">
          <div class="grid grid-cols-3 gap-7 pb-10">
            <ValidationProvider rules="required" name="pickup/dropoff time" v-slot="{ errors }">
            <TimeField
              v-model="formValues.pickupTime"
              class="pb-2"
              >
              Pickup Time
            </TimeField>
            <TimeField
              v-model="formValues.dropoffTime"
              >
              Drop-Off Time
            </TimeField>
            <span
              class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
              name="error-text"
            >
              {{ errors[0] }}
            </span>
          </ValidationProvider>
          </div>
        </div>
        <div class="container">
          <label class="block font-bold mb-2">
            Notes / Other Instructions for the Vendor
          </label>
          <TextArea 
            v-model="formValues.vendorNotes"
          >
          </TextArea>
        </div>
        <div class="container">
          <div class="grid grid-cols-2 gap-7 mt-4 ">
            <div>
              <label class="text-left block font-bold pb-2">Equipment and Special Needs</label>
              <SpecialNeedsSelection
                class="text-sm font-bold"
                :defaultValues="defaultNeed.specialNeeds"
                v-model="formValues.specialNeeds"
                :disabled="true"
                @input="specialChange"
              >
            </SpecialNeedsSelection>
            </div>
            <div class="font-bold flex flex-col gap-1">
              <label class="text-left block font-bold">Student Release</label>
                <ValidationProvider rules="required" name="student release" v-slot="{ errors }">
                  <RadioField
                    class="text-sm font-bold"
                    :disabled="true"
                    v-model="formValues.studentRelease"
                    :value="defaultValues.studentRelease"
                    :options="studentRelease"
                    :alignment="'col'"
                  >
                  </RadioField>
                  <span
                    class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                    name="error-text">
                    {{ errors[0] }}
                  </span>
                </ValidationProvider>
            </div>
            <div class="container">
              <label class="block font-bold mb-2" style="margin-left: -10px;">CST Supervisor Approving Trip</label>
              <div class="grid grid-cols-1 gap-3 mt-2" style="margin-left: -10px;">
                <ValidationProvider rules="required" name="CST Supervisor" v-slot="{ errors }">
                  <div v-for="supervisor in formattedSupervisors" :key="supervisor.value">
                    <label :for="supervisor.value" class="text-sm font-bold pb-2">
                      <input
                        type="radio"
                        :id="supervisor.value"
                        v-model="formValues.supervisor"
                        :value="supervisor.value"
                        class="mr-2"
                      />
                      {{ supervisor.label }}
                    </label>
                  </div>
                  <span
                    class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                    name="error-text"
                  >
                    {{ errors[0] }}
                  </span>
                </ValidationProvider>
              </div>
            </div>
            <div class="container">
              <label class="block font-bold mb-2">
                Reason for Request
              </label>
              <ValidationProvider rules="required" name="Reason for Request" v-slot="{ errors }">
                <TextArea 
                  v-model="formValues.requestReason"
                >
                </TextArea>
                <span
                    class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                    name="error-text"
                  >
                    {{ errors[0] }}
                  </span>
              </ValidationProvider>
            </div>
          </div>
          <div class="container">
            <label class="block font-bold mb-2" style="margin-left: -10px;">Where did you receive this request?</label>
            <div class="grid grid-cols-1 gap-3 mt-2" style="margin-left: -10px;">
              <ValidationProvider rules="required" name="notified" v-slot="{ errors }">
                <div v-for="(option, index) in notified" :key="index">
                  <label :for="'notified-' + index" class="text-sm font-bold pb-1">
                    <input
                      type="radio"
                      :id="'notified-' + index"
                      v-model="formValues.notified"
                      :value="option.label"
                      class="mr-2"
                    />
                    {{ option.label }}
                  </label>
                </div>
                <span
                  class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                  name="error-text">
                  {{ errors[0] }}
                </span>
              </ValidationProvider>

              <div v-if="formValues.notified" class="mt-2">
                <label class="block font-bold mb-1">Link to communication:</label>
                <input
                  type="text"
                  v-model="formValues.linkToCommunication"
                  class="border border-gray-300 rounded px-2 py-1 w-full mb-4"
                  placeholder="Enter the communication link here"
                />
              </div>
            </div>
          </div>
          <div class="container">
            <ValidationProvider rules="required" name="notified" v-slot="{ errors }">
              <label class="block font-bold mb-2" style="margin-left: -10px;">
                Who is covering the cost of the OTR?
              </label>
              <div class="text-sm font-bold pb-4" style="margin-left: -10px;">
                <div v-if="!isOtrA">
                  <input
                    type="radio"
                    id="cst_our_fault"
                    name="covering_cost"
                    value="CST--Our Fault"
                    v-model="formValues.coveringCost"
                  />
                  <label for="cst_our_fault" class="ml-2">CST--Our Fault</label>
                </div>
                <div>
                  <input
                    type="radio"
                    id="district_client"
                    name="covering_cost"
                    value="DISTRICT/CLIENT"
                    v-model="formValues.coveringCost"
                  />
                  <label for="district_client" class="ml-2">
                    DISTRICT/CLIENT (AUTHORIZED representative must be listed below.)
                  </label>
                </div>
                <div v-if="!isOtrA">
                  <input
                    type="radio"
                    id="cst_vendor_issue"
                    name="covering_cost"
                    value="CST/VENDOR"
                    v-model="formValues.coveringCost"
                  />
                  <label for="cst_vendor_issue" class="ml-2">
                    CST--Vendor Performance Issue (Vendor must be listed below. Be sure to submit a Non-Performance Report.)
                  </label>
                </div>
                <span
                  class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                  v-if="errors[0]"
                >
                  {{ errors[0] }}
                </span>
              </div>
            </ValidationProvider>
          </div>

          <div v-if="formValues.coveringCost === 'DISTRICT/CLIENT'" class="mt-2">
            <label for="district_rep" class="block font-bold mb-2" style="margin-left: -10px;">
              District/Client Representative Approving Trip
              <span class="block text-sm font-normal pb-2">
                (This must be an AUTHORIZED representative of the district/client--not simply a staff member.)
              </span>
            </label>
            <div class="container mb-" style="margin-left: -10px;">
              <TextArea
                class="pb-4"
                style="margin-left: -15px;"
                v-model="formValues.districtRep"
              >
              </TextArea>
            </div>
          </div>
          <div v-if="formValues.coveringCost === 'CST/VENDOR'" class="mt-2" style ="margin-left:-10px">
            <label for="vendor-dropdown" class="block font-bold mb-4">
              Original Vendor at Fault
              <span class="block text-sm font-normal">
                Specify VEHICLE, SCHOOL, AM/PM
              </span>
            </label>
            <div class="grid grid-cols-3 gap-4 items-center">
              <div>
                <label for="vendor" class="block font-bold mb-2">Vendor</label>
                <SelectionField
                  class="text-sm font-bold pb-4"
                  v-model="formValues.selectedVendor"
                  @input="fetchVehicles()"
                  :options="vendorOptions"
                  label="label"
                  :key="vendor.id"
                  placeholder="Select Vendor"
                />
              </div>
              <div>
                <label for="vehicle" class="block font-bold mb-2">Vehicle</label>
                <SelectionField
                  class="text-sm font-bold pb-4"
                  v-model="formValues.selectedVehicle"
                  :options="vehicleOptions"
                  label="label"
                  :key="vehicle.id"
                  placeholder="Select Vehicle"
                />
              </div>
              <div class="flex items-center justify-end mt-2">
                <label class="font-bold mr-8">Route</label>
                <label class="inline-flex items-center mr-6">
                  <input
                    type="radio"
                    v-model="formValues.route"
                    value="inbound"
                    class="form-radio"
                  />
                  <span class="ml-2">Inbound</span>
                </label>
                <label class="inline-flex items-center">
                  <input
                    type="radio"
                    v-model="formValues.route"
                    value="outbound"
                    class="form-radio"
                  />
                  <span class="ml-2">Outbound</span>
                </label>
              </div>
            </div>
          </div>
        </div>
        <div class="flex flex-row">
          <span class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
            name="error-text"
            v-if="displayFormHasErrors(errors)">
            There are errors in fields above.
          </span>
        </div>
        <SubmitButton
          class="rounded-md h-10 !shadow m-1"
          type="submit"
          :color="'blue'"
          :size="'md'">
          Submit Form
        </SubmitButton> 
      </form>
    </ValidationObserver>
    <div v-if="displayEndpoint">
      <p>Form Values: {{ formValues }}</p>
      <p>Default Values: {{ defaultValues }}</p>
    </div>
  </div>
</template>
  
<script>
import DropdownLists from '../components/DropdownLists.json'
import CalendarField from '../components/formComponents/CalendarField.vue'
import TextField from './formComponents/TextField.vue'
import SelectionField from './formComponents/SelectionField.vue'
import StudentAddress from '../components/StudentAddress.vue'
import TimeField from './formComponents/TimeField.vue'
import SpecialNeedsSelection from './SpecialNeedsSelection.vue'
import RadioField from './formComponents/RadioField.vue'
import TextArea from './formComponents/TextArea.vue'
import SubmitButton from '../components/SubmitButton.vue'

import user from '../services/user/user.js'
import vendors from '../services/vendors.js'

import oneTimeRideFormService from '../services/pages/oneTimeRideForm.js'

import '../services/validation.js'

export default {
  components: {
    CalendarField,
    TextField,
    SelectionField,
    StudentAddress,
    TimeField,
    SpecialNeedsSelection,
    RadioField,
    TextArea,
    SubmitButton,
  },
  props: {
    defaultValues: {
      type: Object,
      default: () => ({}),
    },
    gradeList: {
      type: Object,
      required: true
    },
    studentDistrictSelectionOptions: {
      type: Array,
      default: undefined,
    },
  },
  data() {
    return {
      displayEndpoint: process.env.NODE_ENV !== 'production',

      districtOptions: this.districtOptions || [],
      studentDistrictList: this.districtOptions || [],
      districtSelectionType: '',
      currentAddress: '',
      currentAddressType: '',
      vendor: '',
      defaultNeed: {},
      canSubmit: true,
      notified: [
        { value: "slack", label: "Slack" },
        { value: "email_drag", label: "Drag/Email" },
        { value: "phone_message", label: "Phone Message" },
        { value: "other", label: "Other" },
      ],
      isOtrA: false,
      student: {},
      formValues: {
        address:{},
        requestingDistrict: null,
        tripDate: '',
        isInbound: null,
        vendorDesignated: '',
        firstName: '',
        lastName: '',
        grade: this.selectedGrade || '',
        school: '',
        pickupTime: '',
        dropoffTime: '',
        vendorNotes: '',
        specialNeeds: '',
        studentRelease: '',
        supervisor: '',
        requestReason:'',
        notified:'',
        linkToCommunication:'',
        coveringCost:'',
        districtRep:'',
        selectedVendor:'',
        selectedVehicle:'',
        route:'',
      },
      schoolList: [],
      districtList: [],
      selectedDistrict: {},
      specialNeeds: [],
      specialNeedsOptions: DropdownLists.specialNeeds,
      studentRelease: DropdownLists.studentRelease,
      user: {},
      selectedVendor: null,
      vendorData: [],
      vehicleList: [],
      supervisors: [],
      formattedSupervisors: [],
      selectedSchool: null,
      vehicleData: [],
      vehicle:'',
    }
  },
  computed: {
    formattedGradeList() {
      return Object.keys(this.gradeList).map(key => {
        return {
          value: parseInt(key),
          label: this.gradeList[key]
        }
      })
    },
    vendorOptions() {
      return Array.isArray(this.vendorData)
        ? this.vendorData.map(vendor => ({
            value: vendor.id,
            label: `${vendor.abbreviation} - ${vendor.partition.description}`,
          }))
        : []
    },
    vehicleOptions() {
      return Array.isArray(this.vehicleData)
        ? this.vehicleData.map(vehicle => ({
            value: vehicle.id,
            label: `${vehicle.code}`,
          }))
        : []
    },
  },
  methods: {
    today() {
      var date = new Date()
      var month = date.getMonth() + Number(1)
      if (month < 10) {
        month = '0'+month
      }
      return date.getFullYear() +'-'+ month +'-'+ date.getDate()
    },
    maxDate() {
      var date = new Date()
      date.setDate(date.getDate() + 14)
      var month = date.getMonth() + 1
      var day = date.getDate()
      if (month < 10) {
        month = '0' + month
      }
      if (day < 10) {
        day = '0' + day
      }
      return date.getFullYear() + '-' + month + '-' + day
    },
    setDefault() {
      if (this.defaultValues) {
        this.setSpecialNeeds()
      }
    },
    displayFormHasErrors(errorsMap) {
      return Object.values(errorsMap).flat().length > 0
    },
    specialChange(value) {
      if (this.suppressBoosterSeatWatch) {
        this.suppressBoosterSeatWatch = false
      }
      if (value && value.needsList && Array.isArray(value.needsList)) {
        this.$emit('boosterSeatSelected', value.needsList.indexOf('infoBoosterSeat') > -1)
      } else {
        console.error('Expected value to have a needsList array, but got:', value)
      }
    },
    setSpecialNeeds() {
      this.defaultNeed.specialNeeds = { needsList: [] }
      const needsList = [
        'infoAide',
        'infoWheelchair',
        'infoSolo',
        'infoEnclosedVehicle',
        'infoBoosterSeat',
        'infoSafetyVest',
        'infoSafetyBeltLockingCover',
        'infoCarSeat',
      ]
      needsList.forEach((need) => {
        if (this.defaultValues[need]) {
          this.defaultNeed.specialNeeds.needsList.push(need)
          if (need === 'infoAide') {
            this.defaultNeed.specialNeeds.aide = this.defaultValues.infoAide
            this.defaultNeed.specialNeeds.aideOnetoOne =
              this.defaultValues.aide === 'A1:1'
          }
        }
      })
      this.defaultNeed.specialNeeds = { ...this.defaultNeed.specialNeeds }
    },
    onGradeChange() {
      this.$emit('gradeSelected', parseInt(this.formValues.grade))
    },
    async fetchSupervisors() {
      try {
        const supervisors = await oneTimeRideFormService.getSupervisorList()

        this.supervisors = supervisors
        this.formattedSupervisors = this.supervisors.map(supervisor => ({
          label: `${supervisor.first_name} ${supervisor.last_name}`,
          value: supervisor.id.toString(),
        }))
      } catch (error) {
        console.error('Error fetching supervisors:', error)
      }
    },
    async fetchVendors() {
      try {
        const response = await vendors.getAllVendors()
        if (response && Array.isArray(response.data)) {
          this.vendorData = response.data
        } else {
          console.error('Fetched data is not an array:', response)
        }
      } catch (error) {
        console.error('Failed to fetch vendor data:', error)
      }
    },
    async fetchVehicles(){
      try {
        const response = await vendors.getVehiclesFromVendor(this.formValues.selectedVendor.value)
        if (response && Array.isArray(response.data)) {
          this.vehicleData = response.data
        } else {
          console.error('Fetched data is not an array:', response)
        }
      } catch (error) {
        console.error('Failed to fetch vehicle data:', error)
      }
    },
    async fetchDistricts() {
      try {
        const districts = await oneTimeRideFormService.getDistrict()

        if (Array.isArray(districts)) {
          this.districtList = districts.map(district => ({
            label: `${district.code}`,
            value: district.code,
          }))

          if (this.defaultValues && this.defaultValues.district) {
            const defaultDistrict = this.districtList.find(
              (district) => district.value === this.defaultValues.district.code
            )
            if (defaultDistrict) {
              this.selectedDistrict = defaultDistrict
              this.formValues.requestingDistrict = defaultDistrict.value
            }
          }
        } else {
          console.error('Districts data is not in expected format:', districts)
        }
      } catch (error) {
        console.error('Error fetching districts:', error)
      }
    },
    async fetchBuildings() {
      try {
        const buildings = await oneTimeRideFormService.getBuilding()

        if (Array.isArray(buildings)) {
          this.schoolList = buildings.map(building => ({
            label: `${building.name}`,
            value: building.name,
          }));

          if (this.defaultValues && this.defaultValues.building) {
            this.selectedSchool = this.defaultValues.building.name
          }
        } else {
          console.error('Buildings data is not in expected format:', buildings)
        }
      } catch (error) {
        console.error('Error fetching buildings:', error)
      }
    },
    setSchool() {
      this.formValues.school = this.selectedSchool
      this.updateValue()
    },
    setDistrict() {
      this.formValues.requestingDistrict = this.selectedDistrict
      this.updateValue()
    },
    setDefaults() {
      Object.keys(this.formValues).forEach((key) => {
        if (this.defaultValues[key] !== undefined) {
          this.formValues[key] = this.defaultValues[key]
        }
      })
    },
    updateValue() {
      this.$emit('input', this.formValues)
    },
    async onSubmit(formInvalid) {
      let hasError = this.scrollToFirstError()
      if (formInvalid || hasError) {
        return false
      }
      try {
        const formData = {
          student: this.defaultValues.dbId,
          is_inbound: this.formValues.isInbound,
          requesting_district: this.formValues.requestingDistrict,
          trip_date: this.formValues.tripDate,
          vendor_designated: this.formValues.vendorDesignated,
          first_name: this.formValues.firstName,
          last_name: this.formValues.lastName,
          grade: this.formValues.grade,
          address: this.formValues.address,
          school: this.formValues.school,
          pickup_time: this.formValues.pickupTime,
          dropoff_time: this.formValues.dropoffTime,
          vendor_notes: this.formValues.vendorNotes,
          special_needs: this.formValues.specialNeeds,
          student_release: this.formValues.studentRelease,
          supervisor: this.formValues.supervisor,
          request_reason: this.formValues.requestReason,
          notified: this.formValues.notified,
          link_to_communication: this.formValues.linkToCommunication,
          covering_cost: this.formValues.coveringCost,
          district_rep: this.formValues.districtRep,
          selected_vendor: this.formValues.selectedVendor.value,
          selected_vehicle: this.formValues.selectedVehicle.value,
          route: this.formValues.route,
        }
        const data = await oneTimeRideFormService.postNewOneTimeRideForm(formData)
        this.$emit('formSubmitted', data)
        this.$router.push('StudentList')
      } catch (error) {
        console.error('Error submitting form:', error)
      }
    },
    validateProvidedInfo() {
      this.showCstInternalData = oneTimeRideFormService.canViewInternalCstData(this.user)
      this.canSubmit = oneTimeRideFormService.canSubmitForm(this.user)
    },
    checkUserCanViewPage() {
      if (!this.user.can('view_one_time_ride')) {
        this.$router.push({
          name: 'StudentList'
        })
        return true
      }
    },
    scrollToFirstError() {
      const elems = document.getElementsByName('error-text')
      let hasError = false

      for(let el of Array.from(elems)) {
        if (el.innerText && el.innerText.trim()) {
          hasError = true
          el.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          })
          break
        }
      }
      return hasError
    },  
  },
  async created() {
    await this.fetchVendors()
    this.fetchSupervisors()
    this.fetchBuildings()
    this.setDefaults()
    this.fetchDistricts()
  },
  async mounted() {
    this.user = await user.get()
    if (this.checkUserCanViewPage()) {
      return
    }
    this.validateProvidedInfo()
    this.setDefault()
    this.updateValue()
  },
  watch: {
    defaultValues() {
      this.setSpecialNeeds()
      this.setDefaults()
    },
    formValues() {
      this.updateValue()
    },
    selectedGrade(newGrade) {
      this.formValues.grade = newGrade
    },
    selectedSchool(newSchool) {
      this.formValues.school = newSchool
    },
    selectedDistrict(newDistrict) {
    this.formValues.requestingDistrict = newDistrict ? newDistrict.value : null
    }
  }
}
</script>
